import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Edit,
  Download,
  RefreshCw,
  Calendar,
  User,
  FileText
} from 'lucide-react';
import TableSection from './components/TableSection';
import ChartSection from './components/ChartSection';
import TextSection from './components/TextSection';
import ExportModal from './components/ExportModal';
import ReportParameters from './components/ReportParameters';

const ReportViewer = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [report, setReport] = useState(null);
  const [executionResult, setExecutionResult] = useState(null);
  const [parameters, setParameters] = useState({});
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    loadAndExecuteReport();
  }, [id]);

  const loadAndExecuteReport = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      // Load report config
      const reportResponse = await fetch(`/api/reports/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode
        }
      });

      if (!reportResponse.ok) {
        throw new Error('Failed to load report');
      }

      const reportData = await reportResponse.json();
      const reportConfig = reportData.data.ReportConfig || reportData.data;
      setReport(reportData.data);

      // Initialize parameters with default values
      const initialParams = {};
      if (reportConfig.parameters) {
        reportConfig.parameters.forEach(param => {
          if (param.defaultValue !== undefined && param.defaultValue !== null) {
            initialParams[param.name] = param.defaultValue;
          }
        });
      }
      setParameters(initialParams);

      // Execute report with initial parameters if no required parameters or all have defaults
      const hasRequiredParams = reportConfig.parameters && 
        reportConfig.parameters.some(p => p.required && initialParams[p.name] === undefined);
      
      if (!hasRequiredParams) {
        await executeReport(initialParams);
      }
    } catch (error) {
      console.error('Error loading report:', error);
      toast.error('Failed to load report');
      navigate('/reports');
    } finally {
      setLoading(false);
    }
  };

  const executeReport = async (params = null) => {
    try {
      setExecuting(true);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/reports/${id}/execute`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          parameters: params || parameters
        })
      });

      if (!response.ok) {
        throw new Error('Failed to execute report');
      }

      const data = await response.json();
      setExecutionResult(data.data);
      toast.success('Report executed successfully');
    } catch (error) {
      console.error('Error executing report:', error);
      toast.error(error.message || 'Failed to execute report');
    } finally {
      setExecuting(false);
    }
  };

  const handleRefresh = () => {
    executeReport();
  };

  const handleParameterChange = (newParameters) => {
    setParameters(newParameters);
  };

  const handleExecuteWithParameters = (params) => {
    setParameters(params);
    executeReport(params);
  };

  const handleEdit = () => {
    navigate(`/reports/${id}/edit`);
  };

  const renderSection = (section) => {
    if (section.error) {
      return (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200 font-medium">Error loading section</p>
          <p className="text-red-600 dark:text-red-300 text-sm mt-1">{section.error}</p>
        </div>
      );
    }

    // Apply custom styling
    const customStyle = section.styling?.css ? { style: section.styling.css } : {};
    const customClass = section.styling?.className || '';

    switch (section.type) {
      case 'table':
        return <TableSection section={section} className={customClass} {...customStyle} />;
      case 'chart':
        return <ChartSection section={section} className={customClass} {...customStyle} />;
      case 'text':
        return <TextSection section={section} className={customClass} {...customStyle} />;
      default:
        return (
          <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <p className="text-gray-600 dark:text-gray-400">Unknown section type: {section.type}</p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading report...</p>
        </div>
      </div>
    );
  }

  if (!report || !executionResult) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <FileText className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Report not found</p>
          <button
            onClick={() => navigate('/reports')}
            className="mt-4 btn btn-secondary"
          >
            Back to Reports
          </button>
        </div>
      </div>
    );
  }

  const headerFooterConfig = report.ReportConfig?.globalStyles?.headerFooter || {};
  const headerConfig = headerFooterConfig.header || {};
  const footerConfig = headerFooterConfig.footer || {};
  const showHeader = !!headerConfig.enabled;
  const showFooter = !!footerConfig.enabled;
  const reportDateText = new Date(executionResult.executedAt).toLocaleString();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/reports')}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {report.ReportName}
              </h1>
              {report.Description && (
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                  {report.Description}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={executing}
              className="btn btn-secondary flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${executing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleEdit}
              className="btn btn-secondary flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={() => setShowExportModal(true)}
              className="btn btn-primary flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Metadata */}
        <div className="flex items-center gap-6 mt-3 text-sm text-gray-600 dark:text-gray-400">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" />
            {report.CreatedByName || 'Unknown'}
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Generated: {new Date(executionResult.executedAt).toLocaleString()}
          </div>
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {executionResult.sections.length} sections
          </div>
        </div>

        {/* Parameters Section */}
        {report.ReportConfig?.parameters && report.ReportConfig.parameters.length > 0 && (
          <div className="mt-4">
            <ReportParameters
              parameters={report.ReportConfig.parameters}
              values={parameters}
              onChange={handleParameterChange}
              onExecute={handleExecuteWithParameters}
            />
          </div>
        )}
      </div>

      {/* Report Content - A4 Print Preview */}
      <div className="flex justify-center p-8 bg-white min-h-screen">
        {executing ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Executing report...</p>
            </div>
          </div>
        ) : executionResult ? (
          <div className="w-full max-w-[210mm]">
            {/* A4 Paper Container */}
            <div className="bg-white shadow-2xl print:shadow-none" style={{ minHeight: '297mm' }}>
              {/* Page Header */}
              <div className="border-b border-gray-200 px-12 pt-8 pb-6">
                {showHeader && (
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                      {headerConfig.logoDataUrl && (
                        <img
                          src={headerConfig.logoDataUrl}
                          alt="Company logo"
                          className="h-10 w-auto max-w-[160px] object-contain"
                        />
                      )}
                      {headerConfig.companyName && (
                        <span className="text-sm font-semibold text-gray-900">
                          {headerConfig.companyName}
                        </span>
                      )}
                    </div>
                    {headerConfig.showReportDate && (
                      <div className="text-xs text-gray-500">
                        Report Date: {reportDateText}
                      </div>
                    )}
                  </div>
                )}
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {report.ReportName}
                </h2>
                {report.Description && (
                  <p className="text-gray-600 text-sm">{report.Description}</p>
                )}
              </div>

              {/* Page Content */}
              <div className="px-12 py-8 space-y-8">
                {executionResult.sections.map((section, index) => (
                  <div
                    key={section.id}
                    className={`${index > 0 ? 'pt-8 border-t border-gray-100' : ''}`}
                  >
                    {renderSection(section)}
                  </div>
                ))}
              </div>

              {/* Page Footer */}
              <div className="border-t border-gray-200 px-12 py-4 mt-8">
                {showFooter ? (
                  <div className="flex flex-wrap items-center justify-between gap-4 text-xs text-gray-500">
                    <div className="flex items-center gap-2">
                      {footerConfig.logoDataUrl && (
                        <img
                          src={footerConfig.logoDataUrl}
                          alt="Company logo"
                          className="h-6 w-auto max-w-[120px] object-contain"
                        />
                      )}
                      {footerConfig.companyName && (
                        <span className="font-medium text-gray-700">
                          {footerConfig.companyName}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      {footerConfig.showReportDate && (
                        <span>Report Date: {reportDateText}</span>
                      )}
                      <span>Page 1 of 1</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>{report.ReportName}</span>
                    <span>Page 1 of 1</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <FileText className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Set parameters and click "Run Report" to view results
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          reportId={id}
          reportName={report.ReportName}
          parameters={parameters}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
};

export default ReportViewer;

